// MANISH CUSTOM CODE - HERO SLIDESHOW (manish-cc) - START
// Crossfade between slides, autoplay, prev/next arrows, clickable dots.
//
// THIS UPDATE - VIDEO-DRIVEN ADVANCE TIMING: video slides were advancing
// on the exact same fixed interval as image slides (data-manish-cc-hero-
// interval, default 6s) - completely disconnected from how long the
// video actually is. A video longer than the interval got cut off
// mid-play every time; a shorter one left dead air before advancing. On
// top of that, activating a slide just called video.play() with no
// currentTime reset, so a video that had been paused partway through
// (e.g. re-entering a slide after navigating around) resumed from where
// it left off instead of restarting - not the "plays from the start
// every time it comes into view" behavior that was wanted.
//
// Fixed both:
//   - resetAndPlayVideos() now sets `video.currentTime = 0` before
//     calling play(), every time a slide becomes active - so a video
//     slide always starts from frame one, never mid-way.
//   - Video slides no longer use the fixed interval at all. Instead,
//     scheduleAdvanceForSlide() listens for the video's real `ended`
//     event, then waits AFTER_VIDEO_WAIT_MS (1.5s) before advancing to
//     the next slide - so the video always plays to completion, pauses
//     briefly on its final frame, then moves on. Image slides are
//     untouched and still use the original fixed-interval behavior.
//   - Hover/focus pausing (pre-existing feature) now correctly pauses
//     the video itself (not just the advance timer) so the countdown to
//     "1.5s after it ends" can't silently tick away while a visitor is
//     actively looking at the slide; if the video had already finished
//     exactly while hovered, moving the mouse away resumes the pending
//     advance immediately instead of waiting for an 'ended' event that
//     will never fire again on an already-finished video.
//
// THIS UPDATE (earlier) - IMAGES NOW SELF-CORRECT TOO, NOT JUST VIDEO: the
// earlier fix made video self-correcting - the container's aspect ratio
// gets overwritten the instant the browser reports the video's REAL
// decoded width/height (`loadedmetadata`), so it's never dependent on
// Liquid's pre-computed estimate being exactly right. Images never got
// the same treatment - they relied ONLY on Liquid's `image.aspect_ratio`
// value. Added `bindImageRealDimensions()`, the exact same idea as the
// existing video function: read the actual rendered <img>'s
// naturalWidth/naturalHeight (the real file, not an estimate) the moment
// it's loaded, and correct the container to match exactly.

function initHeroSlideshow(root) {
  if (root.dataset.manishCcHeroInit) return;
  root.dataset.manishCcHeroInit = 'true';

  const slides = Array.from(root.querySelectorAll('[data-manish-cc-hero-slide]'));
  const dots = Array.from(root.querySelectorAll('[data-manish-cc-hero-dot]'));
  const prevBtn = root.querySelector('[data-manish-cc-hero-prev]');
  const nextBtn = root.querySelector('[data-manish-cc-hero-next]');

  if (slides.length === 0) return;

  const isAdaptMode = root.dataset.manishCcHeroHeightMode === 'adapt';

  let activeIndex = slides.findIndex((s) => s.classList.contains('is-active'));
  if (activeIndex === -1) activeIndex = 0;

  let timer = null; // fixed-interval timer, used for image slides only
  let videoEndTimer = null; // "wait ~1.5s after the video ends" timer
  let videoEndedHandler = null; // { video, fn } - so the 'ended' listener can be cleanly removed
  let pendingVideoAdvance = null; // fn to call on mouseleave if the video already finished while hovered
  let isInteracting = false; // true while hovered/focused - gates auto-advance, mirrors prior hover-pause behavior

  const AFTER_VIDEO_WAIT_MS = 1500;

  // Resets every video in the slide to frame one, then plays it - so a
  // video slide always starts from the beginning every time it becomes
  // active, never resuming mid-way from a previous viewing.
  function resetAndPlayVideos(slide) {
    slide.querySelectorAll('video').forEach((video) => {
      try {
        video.currentTime = 0;
      } catch (e) {
        /* Not seekable yet (metadata not loaded) - it'll already start at 0 on its own. */
      }
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          /* Autoplay can be blocked by the browser; poster image still shows. */
        });
      }
    });
  }

  function pauseVideos(slide) {
    slide.querySelectorAll('video').forEach((video) => video.pause());
  }

  function clearVideoEndTimer() {
    if (videoEndTimer) {
      window.clearTimeout(videoEndTimer);
      videoEndTimer = null;
    }
  }

  function detachVideoEndedListener() {
    if (videoEndedHandler) {
      videoEndedHandler.video.removeEventListener('ended', videoEndedHandler.fn);
      videoEndedHandler = null;
    }
    pendingVideoAdvance = null;
  }

  // Sync the outer container's aspect ratio to whichever slide is active,
  // using the pre-computed value from Liquid as an immediate best guess.
  // This is only a starting point now - bindVideoRealDimensions /
  // bindImageRealDimensions below correct it to the real file dimensions
  // as soon as they're known, for both media types.
  function syncAspectRatioFromData(slide) {
    if (!isAdaptMode) return;
    const mobileAspect = slide.dataset.manishCcHeroAspectMobile;
    const desktopAspect = slide.dataset.manishCcHeroAspectDesktop;
    if (mobileAspect) root.style.setProperty('--manish-cc-hero-aspect-mobile', mobileAspect);
    if (desktopAspect) root.style.setProperty('--manish-cc-hero-aspect-desktop', desktopAspect);
  }

  // Self-corrects the container to the video's REAL, actual dimensions
  // (straight from the browser's decoded video metadata) the moment
  // they're known - this is authoritative and overrides the earlier
  // estimate from Liquid, guaranteeing the container ends up matching
  // the real file exactly, regardless of anything computed ahead of time.
  function bindVideoRealDimensions(slide) {
    const mobileVideo = slide.querySelector('.manish-cc-hero__media--mobile-video video');
    const desktopVideo = slide.querySelector('.manish-cc-hero__media--desktop-video video');

    function applyRealAspect(video, cssVar) {
      if (!video) return;
      const apply = () => {
        if (!video.videoWidth || !video.videoHeight) return;
        if (!isAdaptMode) return;
        if (!slide.classList.contains('is-active')) return; // only affects the container while this slide is the one showing
        const ratio = (video.videoWidth / video.videoHeight).toFixed(4);
        root.style.setProperty(cssVar, ratio);
      };

      if (video.readyState >= 1) {
        apply();
      } else {
        video.addEventListener('loadedmetadata', apply, { once: true });
      }
    }

    applyRealAspect(mobileVideo, '--manish-cc-hero-aspect-mobile');
    applyRealAspect(desktopVideo, '--manish-cc-hero-aspect-desktop');
  }

  // Same idea as bindVideoRealDimensions, for image slides. The <picture>
  // element only renders ONE actual <img> - the browser has already
  // picked whichever <source> matches the current breakpoint (mobile or
  // desktop) before this ever runs, so naturalWidth/naturalHeight already
  // reflect the right variant. We just need to know which CSS var
  // (mobile or desktop) that corresponds to right now.
  function bindImageRealDimensions(slide) {
    const img = slide.querySelector('.manish-cc-hero__img');
    if (!img) return;

    const isDesktopViewport = window.matchMedia('(min-width: 750px)').matches;
    const cssVar = isDesktopViewport ? '--manish-cc-hero-aspect-desktop' : '--manish-cc-hero-aspect-mobile';

    const apply = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      if (!isAdaptMode) return;
      if (!slide.classList.contains('is-active')) return;
      const ratio = (img.naturalWidth / img.naturalHeight).toFixed(4);
      root.style.setProperty(cssVar, ratio);
    };

    if (img.complete && img.naturalWidth) {
      apply();
    }
    // Not { once: true } - if the browser swaps <picture> source when the
    // viewport crosses the mobile/desktop breakpoint, a fresh load event
    // fires for the new image and this re-corrects the ratio again.
    img.addEventListener('load', apply);
  }

  const intervalSeconds = parseFloat(root.dataset.manishCcHeroInterval || '6');
  const intervalMs = Math.max(2000, intervalSeconds * 1000);

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function startAutoplay() {
    if (timer) window.clearInterval(timer);
    if (slides.length < 2) return;
    if (prefersReduced.matches) return;
    timer = window.setInterval(() => goTo(activeIndex + 1), intervalMs);
  }

  function stopAutoplay() {
    if (timer) window.clearInterval(timer);
    timer = null;
  }

  // Decides how the CURRENTLY active slide should advance: a video slide
  // waits for the video to actually finish (plus a short pause), an
  // image slide (or reduced-motion) falls back to the fixed interval.
  function scheduleAdvanceForSlide(slide) {
    stopAutoplay();
    clearVideoEndTimer();
    detachVideoEndedListener();

    const video = slide.querySelector('video');

    if (video && !prefersReduced.matches) {
      const triggerAdvance = () => {
        clearVideoEndTimer();
        videoEndTimer = window.setTimeout(() => {
          goTo(activeIndex + 1);
        }, AFTER_VIDEO_WAIT_MS);
      };

      const onEnded = () => {
        if (isInteracting) return; // don't schedule the advance while actively hovered/focused
        triggerAdvance();
      };

      video.addEventListener('ended', onEnded);
      videoEndedHandler = { video, fn: onEnded };

      // If the visitor is still hovering when the video finishes, onEnded
      // above intentionally does nothing. This lets mouseleave trigger the
      // wait-then-advance immediately once they stop hovering, instead of
      // waiting for another 'ended' event that can't fire again on an
      // already-finished video.
      pendingVideoAdvance = () => {
        if (video.ended) triggerAdvance();
      };
    } else {
      startAutoplay();
    }
  }

  function pauseForInteraction() {
    isInteracting = true;
    stopAutoplay();
    clearVideoEndTimer();
    const activeSlide = slides[activeIndex];
    const video = activeSlide.querySelector('video');
    if (video && !video.ended) video.pause();
  }

  function resumeAfterInteraction() {
    isInteracting = false;
    const activeSlide = slides[activeIndex];
    const video = activeSlide.querySelector('video');
    if (video) {
      if (video.ended) {
        if (pendingVideoAdvance) pendingVideoAdvance();
      } else {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      }
    } else {
      startAutoplay();
    }
  }

  function goTo(index) {
    const nextIndex = ((index % slides.length) + slides.length) % slides.length;
    if (nextIndex === activeIndex) return;

    const current = slides[activeIndex];
    const next = slides[nextIndex];

    current.classList.remove('is-active');
    pauseVideos(current);
    clearVideoEndTimer();
    detachVideoEndedListener();

    next.classList.add('is-active');
    resetAndPlayVideos(next);
    syncAspectRatioFromData(next);
    bindVideoRealDimensions(next);
    bindImageRealDimensions(next);

    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === nextIndex));

    activeIndex = nextIndex;

    scheduleAdvanceForSlide(next);
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      goTo(activeIndex - 1);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      goTo(activeIndex + 1);
    });
  }

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      goTo(i);
    });
  });

  root.addEventListener('mouseenter', pauseForInteraction);
  root.addEventListener('mouseleave', resumeAfterInteraction);
  root.addEventListener('focusin', pauseForInteraction);
  root.addEventListener('focusout', resumeAfterInteraction);

  resetAndPlayVideos(slides[activeIndex]);
  syncAspectRatioFromData(slides[activeIndex]);
  bindVideoRealDimensions(slides[activeIndex]);
  bindImageRealDimensions(slides[activeIndex]);

  scheduleAdvanceForSlide(slides[activeIndex]);

  prefersReduced.addEventListener('change', (e) => {
    if (e.matches) {
      stopAutoplay();
      clearVideoEndTimer();
      detachVideoEndedListener();
    } else {
      scheduleAdvanceForSlide(slides[activeIndex]);
    }
  });
}

function initAll() {
  document.querySelectorAll('[data-manish-cc-hero-slideshow]').forEach(initHeroSlideshow);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', initAll);

// MANISH CUSTOM CODE - HERO SLIDESHOW (manish-cc) - END
